import * as React from 'react'
import { Boxes, Plus } from 'lucide-react'
import { useFurniture } from '@/hooks/useFurniture'
import { useFurnitureGroups, useCreateFurnitureGroup, useUpdateFurnitureGroup } from '@/hooks/useFurnitureGroups'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import { FurnitureGroupPreview, GroupEditor } from '@/components/lazyDrawings'
import { Table, TableHeader, TableHead, TableBody, TableRow, TableCell } from '@/components/ui/table'
import { TableThumbCell, TableThumbHead } from '@/components/TableThumbCell'
import { LoadingState, ErrorState, EmptyState } from '@/components/ui/states'
import { FormError } from '@/components/ui/form-error'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { groupSeating } from '@/lib/groupSeating'
import type { FurnitureGroupDto } from '@lib/dto/furniture-group.dto'
import type { FurnitureItemDto } from '@lib/dto/furniture-item.dto'
import type { FurnitureGroupMemberInput } from '@lib/input/furniture-group.input'

/**
 * An arrangement is made of its own venue's pieces.
 *
 * This used to be a set intersection — an arrangement offered at several venues
 * could only hold pieces every one of them had. A venue owns its furniture now,
 * so the question is just "is this piece this venue's".
 */
function itemsForVenue(catalog: FurnitureItemDto[], venueId: string): FurnitureItemDto[] {
  return catalog.filter((i) => i.venueId === venueId)
}

/**
 * Manage reusable furniture groups: a list plus a create/edit dialog that
 * arranges catalog items into a template. Read-only (no create/edit) when
 * `canEdit` is false — a venue admin browses the catalog but can't change it.
 *
 * **Which venue is a parameter, not something read off the session.** It used to
 * come from the session's focus, which is only set for somebody who has entered
 * the venue; an organization admin opening the venue's settings has not, so the
 * panel asked for the arrangements of no venue at all and offered none of its
 * pieces — next to an items tab, on the same screen, listing all of them. The
 * catalog beside it already took the venue from the page for exactly this
 * reason, and this was left behind.
 */
export function FurnitureGroupsPanel({ venueId, canEdit = true }: { venueId: string; canEdit?: boolean }) {
  const groupsQuery = useFurnitureGroups(venueId)
  const furniture = useFurniture(undefined, venueId)
  const createGroup = useCreateFurnitureGroup()
  const updateGroup = useUpdateFurnitureGroup()

  const [open, setOpen] = React.useState(false)
  const [editingId, setEditingId] = React.useState<string | null>(null)
  const [name, setName] = React.useState('')
  const [members, setMembers] = React.useState<FurnitureGroupMemberInput[]>([])
  const [submitError, setSubmitError] = React.useState<string | null>(null)

  const catalog = furniture.data?.items ?? []
  /**
   * This venue's arrangements only.
   *
   * The request already names the venue, so this filter is belt to that braces —
   * kept because what it protects is not cosmetic: opening another venue's
   * arrangement offers none of its pieces (they belong to that venue) while Save
   * validates members against this one, emptying the arrangement with no error.
   */
  const groups = (groupsQuery.data?.groups ?? []).filter((g) => g.venueId === venueId)
  const busy = createGroup.isPending || updateGroup.isPending

  // The venue being worked in owns both the arrangement and the pieces it can
  // hold, so there is nothing left to choose and nothing to intersect.
  const availableItems = itemsForVenue(catalog, venueId)
  // The same tally the arranger shows, so the button and the message under it
  // cannot disagree about whether the arrangement fits.
  const { over: overCapacity } = groupSeating(availableItems, members)

  function openCreate(): void {
    setEditingId(null)
    setName('')
    setMembers([])
    setSubmitError(null)
    setOpen(true)
  }

  function openEdit(group: FurnitureGroupDto): void {
    setEditingId(group.id)
    setName(group.name)
    setMembers(group.members.map((m) => ({ furnitureItemId: m.furnitureItemId, x: m.x, y: m.y, rotation: m.rotation })))
    setSubmitError(null)
    setOpen(true)
  }

  async function save(): Promise<void> {
    setSubmitError(null)
    try {
      if (editingId) {
        await updateGroup.mutateAsync({ id: editingId, input: { name, members } })
      } else {
        await createGroup.mutateAsync({ name, members, venueId })
      }
      setOpen(false)
    } catch (error) {
      setSubmitError(requestErrorMessage(error))
    }
  }

  function renderList() {
    if (groupsQuery.isPending) return <LoadingState label="Loading groups…" />
    if (groupsQuery.isError) {
      return <ErrorState message={requestErrorMessage(groupsQuery.error)} onRetry={() => void groupsQuery.refetch()} />
    }
    // A venue with no arrangements at all is an invitation, not a table with
    // nothing in it — the useful thing there is making the first one, and the
    // full empty state carries that. See web/CLAUDE.md on the two empty cases.
    if (groups.length === 0) {
      return (
        <EmptyState
          icon={Boxes}
          title="No groups yet"
          description={
            canEdit
              ? 'Arrange catalog items into a reusable group — a table with its chairs, say.'
              : 'No groups yet.'
          }
          action={
            canEdit ? (
              <Button onClick={openCreate}>
                <Plus className="h-4 w-4" /> New group
              </Button>
            ) : undefined
          }
        />
      )
    }
    return (
      <Table
        heading={`${String(groups.length)} group${groups.length === 1 ? '' : 's'}`}
        actions={
          canEdit ? (
            <Button variant="ghost" size="sm" onClick={openCreate}>
              <Plus className="h-4 w-4" /> New group
            </Button>
          ) : undefined
        }
      >
        <TableHeader>
          {/* Not "Photograph": an arrangement never has one, and that is what
              a screen reader would otherwise announce over a column of drawings. */}
          <TableThumbHead label="Arrangement" />
          <TableHead>Group</TableHead>
          <TableHead>Pieces</TableHead>
        </TableHeader>
        <TableBody>
          {groups.map((group) => (
            <GroupRow key={group.id} group={group} onOpen={canEdit ? () => openEdit(group) : undefined} />
          ))}
        </TableBody>
      </Table>
    )
  }

  return (
    <div className="space-y-6">
      {renderList()}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit group' : 'New group'}</DialogTitle>
            <DialogDescription>Name it and arrange its pieces.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <Field label="Name" htmlFor="fg-name">
              <Input id="fg-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Reception round" autoFocus />
            </Field>
            <Field label="Pieces">
              <GroupEditor items={availableItems} members={members} onChange={setMembers} />
            </Field>

            {submitError ? <FormError>{submitError}</FormError> : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            {/* An over-seated arrangement is refused by the API, so the button
                that would send it is shut rather than left to fail. The reason is
                already on screen, under the arranger. */}
            <Button pending={busy} type="button" onClick={save} disabled={name.trim() === '' || members.length === 0 || overCapacity !== null}>
              {busy ? 'Saving…' : 'Save group'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

/**
 * One arrangement: its drawing, its name, how many pieces it holds.
 *
 * The whole row opens the editor, for the reason the items table gives: the row
 * is about one thing, and making only its name clickable puts a five-pixel
 * target in a forty-pixel row.
 */
function GroupRow({
  group,
  onOpen,
}: {
  group: FurnitureGroupDto
  onOpen: (() => void) | undefined
}) {
  return (
    <TableRow
      className={onOpen ? 'cursor-pointer' : undefined}
      onClick={onOpen}
      onKeyDown={
        onOpen
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onOpen()
              }
            }
          : undefined
      }
      tabIndex={onOpen ? 0 : undefined}
    >
      <TableThumbCell
        // Always null: an arrangement has no photograph, only its drawing.
        src={null}
        // The arrangement itself, not a symbol for one: a round table ringed by
        // ten chairs is recognizable at this size, and the name alone is not —
        // "Table 96"×40" + 10 chairs" appears four times at one venue.
        fallback={<FurnitureGroupPreview members={group.members} size={32} />}
      />
      <TableCell className="font-medium">{group.name}</TableCell>
      <TableCell className="text-ink-muted">
        {group.members.length} {group.members.length === 1 ? 'piece' : 'pieces'}
      </TableCell>
    </TableRow>
  )
}
